/**
 * Native Push Notifications Service
 * Handles FCM push notifications on iOS and Android using Firebase
 */

import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Badge } from '@capawesome/capacitor-badge'
import { Capacitor } from '@capacitor/core'
import { supabase } from '@/lib/supabase'

const isNative = Capacitor.isNativePlatform()

export interface PushNotificationData {
  type?: string
  habitId?: string
  challengeId?: string
  userId?: string
  url?: string
  [key: string]: unknown
}

type NavigateFunction = (path: string) => void

let navigateFunction: NavigateFunction | null = null

/**
 * Set the navigation function for handling notification taps
 */
export function setNavigateFunction(navigate: NavigateFunction): void {
  navigateFunction = navigate
}

/**
 * Request push notification permissions and register for notifications
 */
export async function requestPushPermissions(): Promise<boolean> {
  if (!isNative) {
    console.log('Push notifications not available on web - use web push instead')
    return false
  }

  try {
    // Check current permission status
    let permStatus = await FirebaseMessaging.checkPermissions()

    if (permStatus.receive === 'prompt') {
      // Request permission
      permStatus = await FirebaseMessaging.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      console.log('Push notification permission denied')
      return false
    }

    // Get FCM token
    const { token } = await FirebaseMessaging.getToken()
    console.log('FCM token obtained:', token.substring(0, 30) + '...')
    await saveFCMToken(token)
    
    return true
  } catch (error) {
    console.error('Error requesting push permissions:', error)
    return false
  }
}

/**
 * Initialize push notification listeners
 */
export async function initPushNotifications(): Promise<void> {
  if (!isNative) {
    console.log('Skipping native push init - not on native platform')
    return
  }

  // Handle token refresh
  FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
    console.log('FCM token refreshed:', token.substring(0, 30) + '...')
    await saveFCMToken(token)
  })

  // Handle push notification received while app is in foreground
  FirebaseMessaging.addListener('notificationReceived', (notification) => {
    console.log('Push notification received:', notification)
    // The notification data is in notification.notification and notification.data
  })

  // Handle notification tap (app opened from notification)
  FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
    console.log('Push notification action performed:', action)
    handleNotificationTap(action.notification.data as PushNotificationData)
  })

  // NOTE: Push permission is NOT requested here.
  // It is requested reactively once the user has at least one habit
  // (see AuthenticatedLayout).
}

/**
 * Save FCM token to database.
 *
 * Handles cross-platform switching: when a user moves from Android to iOS
 * (or vice versa), all previous FCM subscriptions are deactivated so only
 * the current device receives push notifications.
 */
async function saveFCMToken(fcmToken: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      console.log('No user logged in, skipping FCM token save')
      return
    }

    const deviceType = Capacitor.getPlatform() // 'ios' or 'android'
    const endpoint = `fcm://${deviceType}/${fcmToken.substring(0, 20)}`

    // Deactivate ALL existing FCM subscriptions for this user (any device type).
    // This ensures that when switching platforms (e.g. Android -> iOS), stale
    // tokens from the old device don't accumulate or receive failed sends.
    const { error: deactivateError } = await supabase
      .from('push_subscriptions')
      .update({ is_active: false })
      .eq('user_id', user.id)
      .not('fcm_token', 'is', null)

    if (deactivateError) {
      console.warn('Error deactivating old FCM subscriptions:', deactivateError)
    }

    // Check if we already have a row for this exact device type
    const { data: existing } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', user.id)
      .eq('device_type', deviceType)
      .not('fcm_token', 'is', null)
      .maybeSingle()

    if (existing) {
      const { error } = await supabase
        .from('push_subscriptions')
        .update({
          fcm_token: fcmToken,
          endpoint,
          is_active: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)

      if (error) {
        console.error('Error updating FCM token:', error)
      } else {
        console.log(`FCM token updated for ${deviceType}`)
      }
    } else {
      // Placeholders for required web-push columns (not used for FCM subscriptions)
      const { error } = await supabase
        .from('push_subscriptions')
        .insert({
          user_id: user.id,
          endpoint,
          p256dh: 'fcm-native',
          auth: 'fcm-native',
          fcm_token: fcmToken,
          device_type: deviceType,
          is_active: true,
        })

      if (error) {
        console.error('Error inserting FCM token:', error)
      } else {
        console.log(`FCM token saved for ${deviceType}`)
      }
    }
  } catch (error) {
    console.error('Error saving FCM token:', error)
  }
}

/**
 * Handle notification tap - navigate to appropriate screen
 */
function handleNotificationTap(data: PushNotificationData): void {
  if (!navigateFunction) {
    console.warn('Navigate function not set')
    return
  }

  const { type, habitId, challengeId, userId, url } = data

  // If a URL is provided, navigate directly to it
  if (url) {
    navigateFunction(url)
    return
  }

  // Navigate based on notification type
  switch (type) {
    case 'habit_reminder':
      if (habitId) {
        navigateFunction(`/?habit=${habitId}`)
      } else {
        navigateFunction('/')
      }
      break

    case 'challenge_invite':
    case 'challenge_update':
      if (challengeId) {
        navigateFunction(`/challenges/${challengeId}`)
      } else {
        navigateFunction('/challenges')
      }
      break

    case 'new_follower':
    case 'follow_request':
      if (userId) {
        navigateFunction(`/u/${userId}`)
      } else {
        navigateFunction('/community')
      }
      break

    case 'milestone':
    case 'badge_earned':
      navigateFunction('/profile')
      break

    case 'streak_milestone':
      navigateFunction('/')
      break

    case 'level_up':
      navigateFunction('/profile')
      break

    case 'admin_broadcast':
    case 'admin_message':
      navigateFunction('/')
      break

    case 'partner_request':
    case 'partner_accepted':
      navigateFunction('/community?tab=partner')
      break

    case 'partner_completed':
    case 'partner_streak':
    case 'partner_nudge':
      navigateFunction('/community?tab=partner')
      break

    default:
      // Default to home screen (notifications are in the bell drawer)
      navigateFunction('/')
  }
}

/**
 * Remove FCM token (on logout)
 */
export async function removeFCMToken(): Promise<void> {
  if (!isNative) return

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Delete the FCM token from Firebase
    await FirebaseMessaging.deleteToken()

    // Deactivate all FCM tokens for this user on this device
    await supabase
      .from('push_subscriptions')
      .update({ is_active: false })
      .eq('user_id', user.id)
      .not('fcm_token', 'is', null)

    console.log('FCM token deactivated')
  } catch (error) {
    console.error('Error removing FCM token:', error)
  }
}

/**
 * Get current push notification permission status
 */
export async function getPushPermissionStatus(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isNative) return 'denied'

  try {
    const status = await FirebaseMessaging.checkPermissions()
    // Map 'prompt-with-rationale' to 'prompt' for simpler return type
    const receive = status.receive
    if (receive === 'prompt-with-rationale') return 'prompt'
    return receive as 'granted' | 'denied' | 'prompt'
  } catch (error) {
    console.error('Error checking push permissions:', error)
    return 'denied'
  }
}

/**
 * Check if push notifications are available and enabled
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!isNative) return false
  const status = await getPushPermissionStatus()
  return status === 'granted'
}

/**
 * Clear the app badge count (iOS home screen badge)
 */
export async function clearBadgeCount(): Promise<void> {
  if (!isNative) return

  try {
    // Clear the app icon badge on home screen
    await Badge.clear()
    
    // Also remove all delivered notifications from notification center
    await FirebaseMessaging.removeAllDeliveredNotifications()
    
    console.log('Badge count and notifications cleared')
  } catch (error) {
    console.error('Error clearing badge count:', error)
  }
}

/**
 * Sync notifications - clear badge and remove orphan notifications
 * Call this when user views the notifications page
 */
export async function syncNotifications(): Promise<void> {
  if (!isNative) return

  try {
    // Clear badge count since user is viewing notifications
    await clearBadgeCount()
    
    // Clean up orphan notifications (server-side)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Delete old habit_reminder notifications (older than 24 hours)
    const oneDayAgo = new Date()
    oneDayAgo.setDate(oneDayAgo.getDate() - 1)
    
    await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id)
      .eq('type', 'habit_reminder')
      .lt('created_at', oneDayAgo.toISOString())

    console.log('Notifications synced')
  } catch (error) {
    console.error('Error syncing notifications:', error)
  }
}

/**
 * Update badge count to match unread notifications
 */
export async function updateBadgeCount(): Promise<void> {
  if (!isNative) return

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Get unread count
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)

    // Set badge to unread count
    await Badge.set({ count: count || 0 })
    console.log('Badge updated to:', count || 0)
  } catch (error) {
    console.error('Error updating badge count:', error)
  }
}
